import React, { useMemo, useRef, useState } from 'react';
import { components } from 'react-select';
import { format } from 'date-fns';
import { THEME } from './theme/themeColors';


export const CustomCheckboxOption = (props) => (
  <components.Option {...props}>
    <input
      type="checkbox"
      checked={props.isSelected}
      onChange={() => {}}
      style={{ marginRight: '8px', accentColor: THEME.primary }}
    />
    <label style={{ margin: 0, cursor: 'pointer' }}>{props.label}</label>
  </components.Option>
);


export const CustomValueContainer = ({ children, getValue, selectProps, ...props }) => {
  const selected = getValue();
  const hasSelectAll = selected.some(o => o.value === 'all');
  const realSelected = selected.filter(o => o.value !== 'all');

  let displayText = null;
  if (hasSelectAll) {
    displayText = 'All Selected';
  } else if (realSelected.length > 0) {
    displayText = `${realSelected.length} ${realSelected.length === 1 ? 'item' : 'items'} selected`;
  }

  const inputChild = React.Children.toArray(children).find(
    (child) => child?.type?.name === 'Input' || child?.props?.id?.startsWith('react-select')
  );

  return (
    <components.ValueContainer getValue={getValue} selectProps={selectProps} {...props}>
      {displayText
        ? <span style={{ fontSize: '13px', color: '#333', whiteSpace: 'nowrap' }}>{displayText}</span>
        : null}
      {inputChild}
    </components.ValueContainer>
  );
};


export const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '30px',
    height: '30px',
    fontSize: '13px',
    padding: '1px 0',
    borderRadius: '4px',
    backgroundColor: '#fff',
    borderColor: state.isFocused ? THEME.primary : '#ced4da',
    boxShadow: 'none',
    '&:hover': { borderColor: state.isFocused ? THEME.primary : THEME.alpha(0.45) },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '2px 6px',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '13px',
    padding: '6px 12px',
    backgroundColor: state.isFocused ? THEME.alpha(0.08) : '#fff',
    color: '#333',
    cursor: 'pointer',
  }),
  menuList: (base) => ({ ...base, padding: '4px 0', maxHeight: '220px' }),
  multiValue: () => ({ display: 'none' }),
  multiValueLabel: () => ({ display: 'none' }),
  multiValueRemove: () => ({ display: 'none' }),
  indicatorsContainer: (base) => ({
    ...base,
    padding: '0 4px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 4px',
    color: THEME.primary,
    '&:hover': { color: THEME.primary },
  }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px', color: THEME.primary }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  }),
  placeholder: (base) => ({ ...base, fontSize: '13px', color: '#999' }),
};


export const buildSortedOptions = (baseOptions, selectedValues) => {
  const selectedSet = new Set(selectedValues.map(o => o.value));
  const selectAllOpt = baseOptions.find(o => o.value === 'all');
  const realOpts = baseOptions.filter(o => o.value !== 'all');
  const selectedReal = realOpts.filter(o => selectedSet.has(o.value));
  const unselectedReal = realOpts.filter(o => !selectedSet.has(o.value));
  return [selectAllOpt, ...selectedReal, ...unselectedReal].filter(Boolean);
};


export const useMultiSelect = (baseOptions, key, autoSelectFirst = false) => {
  const [selected, setSelected] = useState([]);
  const prevRef = useRef(null);
  const autoSelectedRef = useRef(false); 

  if (autoSelectFirst && !autoSelectedRef.current && baseOptions.length > 0 && selected.length === 0) {
    const first = baseOptions.find(o => o.label === 'Open');
    if (first) { autoSelectedRef.current = true; Promise.resolve().then(() => setSelected([first])); }
  }

  const sortedOptions = useMemo(
    () => buildSortedOptions(baseOptions, selected),
    [baseOptions, selected]
  );

  const onChange = (newSelected) => {
    if (!newSelected || newSelected.length === 0) {
      prevRef.current = [];
      setSelected([]);
      return;
    }

    const allRealOptions = baseOptions.filter(o => o.value !== 'all');
    const selectAllPresent = newSelected.some(o => o.value === 'all');
    const realSelected = newSelected.filter(o => o.value !== 'all');
    const prev = prevRef.current ?? [];
    const prevHadSelectAll = prev.some(o => o.value === 'all');

    let next;
    if (selectAllPresent && !prevHadSelectAll) {
      next = baseOptions;
    } else if (!selectAllPresent && prevHadSelectAll) {
      next = [];
    } else {
      next = realSelected.length === allRealOptions.length ? baseOptions : realSelected;
    }

    prevRef.current = next;
    setSelected(next);
  };

  const reset = () => {
    prevRef.current = [];
    setSelected([]);
  };

  const props = (placeholder) => ({
    isMulti: true,
    closeMenuOnSelect: false,
    hideSelectedOptions: false,
    options: sortedOptions,
    value: selected,
    onChange,
    placeholder,
    className: 'react-select-container react-select-small',
    classNamePrefix: 'react-select',
    components: { Option: CustomCheckboxOption, ValueContainer: CustomValueContainer },
    menuPortalTarget: document.body,
    styles: selectStyles,
    noOptionsMessage: () => 'No option available',
  });


  const values = selected.filter(o => o.value !== 'all').map(o => o.value);

  return { selected, setSelected, sortedOptions, onChange, reset, props, values };
};

export const getDateRangeForPreset = (preset) => {
  const today = new Date();
  let from = '', to = '';

  switch (preset) {
    case '1':
      from = to = format(today, 'yyyy-MM-dd');
      break;
    case '2': {
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay());
      from = format(start, 'yyyy-MM-dd');
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      to = format(end, 'yyyy-MM-dd');
      break;
    }
    case '3':
      from = format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd');
      to   = format(new Date(today.getFullYear(), today.getMonth() + 1, 0), 'yyyy-MM-dd');
      break;
    case '4': {
      const q = Math.floor(today.getMonth() / 3);
      from = format(new Date(today.getFullYear(), q * 3, 1), 'yyyy-MM-dd');
      to   = format(new Date(today.getFullYear(), q * 3 + 3, 0), 'yyyy-MM-dd');
      break;
    }
    case '5':
      from = format(new Date(today.getFullYear(), 0, 1), 'yyyy-MM-dd');
      to   = format(new Date(today.getFullYear(), 11, 31), 'yyyy-MM-dd');
      break;
    case '6': {
      const lwStart = new Date(today);
      lwStart.setDate(today.getDate() - today.getDay() - 7);
      from = format(lwStart, 'yyyy-MM-dd');
      const lwEnd = new Date(lwStart);
      lwEnd.setDate(lwStart.getDate() + 6);
      to = format(lwEnd, 'yyyy-MM-dd');
      break;
    }
    case '7': {
      const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      from = format(lm, 'yyyy-MM-dd');
      to   = format(new Date(today.getFullYear(), today.getMonth(), 0), 'yyyy-MM-dd');
      break;
    }
    case '8':
      from = format(new Date(today.getFullYear() - 1, 0, 1), 'yyyy-MM-dd');
      to   = format(new Date(today.getFullYear() - 1, 11, 31), 'yyyy-MM-dd');
      break;
    case '9': {
      const lq = Math.floor((today.getMonth() - 3) / 3);
      from = format(new Date(today.getFullYear(), lq * 3, 1), 'yyyy-MM-dd');
      to   = format(new Date(today.getFullYear(), lq * 3 + 3, 0), 'yyyy-MM-dd');
      break;
    }
    default:
      break;
  }

  return { fromDate: from, toDate: to };
};

export const useDatePreset = () => {
  const [preset, setPreset]     = useState('0');
  const [fromDate, setFromDate] = useState('');
  const [toDate,   setToDate]   = useState('');

  const applyPreset = (value) => {
    setPreset(value);
    const { fromDate: f, toDate: t } = getDateRangeForPreset(value);
    setFromDate(f);
    setToDate(t);
  };

  const setRange = (start, end) => {
    setFromDate(start ? format(start, 'yyyy-MM-dd') : '');
    setToDate(end   ? format(end,   'yyyy-MM-dd') : '');
  };

  const reset = () => {
    setPreset('0');
    setFromDate('');
    setToDate('');
  };

  return { preset, fromDate, toDate, setFromDate, setToDate, applyPreset, setRange, reset };
};